<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
                            <h4 class="text-capitalize fw-500 breadcrumb-title">
                                <i class="fas fa-headset"></i> Tiket Support Saya
                            </h4>
                        </div>
                        <div class="action-btn">
                            <button @click="newTicketModal = true" class="btn btn-primary">
                                <i class="fas fa-plus"></i> Buat Tiket Baru
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Working hours banner -->
            <div :class="['alert', workingHours.is_open ? 'alert-success' : 'alert-secondary', 'd-flex', 'align-items-center']">
                <i :class="workingHours.is_open ? 'fas fa-circle text-success' : 'fas fa-moon'" style="margin-right:10px;"></i>
                <div>
                    <strong>{{ workingHours.is_open ? 'Tim Support Online' : 'Saat ini di luar jam kerja' }}</strong>
                    — {{ workingHours.info }}
                </div>
            </div>

            <!-- Filter -->
            <div class="card p-3 mb-3">
                <div class="row align-items-center">
                    <div class="col-md-4">
                        <select v-model="filter.status" @change="loadList" class="form-control form-control-sm">
                            <option value="">Semua Status</option>
                            <option value="open">Open</option>
                            <option value="in_progress">Diproses</option>
                            <option value="waiting_user">Menunggu Anda</option>
                            <option value="closed">Selesai</option>
                        </select>
                    </div>
                    <div class="col-md-6">
                        <input v-model="filter.search" @input="onSearchInput" class="form-control form-control-sm" placeholder="Cari subject...">
                    </div>
                    <div class="col-md-2 text-right">
                        <button @click="loadList" class="btn btn-sm btn-light">
                            <i class="fas fa-sync"></i> Refresh
                        </button>
                    </div>
                </div>
            </div>

            <!-- List -->
            <div v-if="loading" class="text-center p-5">
                <div class="spinner-border"></div>
            </div>
            <div v-else-if="filteredTickets.length === 0" class="card p-5 text-center text-muted">
                <i class="fas fa-inbox fa-3x mb-3" style="color:#d1d5db;"></i>
                <h5>Belum ada tiket</h5>
                <p>Klik tombol "Buat Tiket Baru" di atas untuk mulai.</p>
            </div>
            <div v-else>
                <router-link v-for="t in filteredTickets" :key="t.id" :to="'/support/ticket/' + t.id" class="ticket-card">
                    <div class="row align-items-center">
                        <div class="col-md-7">
                            <div class="d-flex align-items-center">
                                <span class="status-badge" :class="'status-' + t.status">{{ statusLabel(t.status) }}</span>
                                <h5 class="mb-0 ml-2 ticket-subject">
                                    {{ t.subject }}
                                    <span v-if="t.user_unread_count > 0" class="unread-pill">{{ t.user_unread_count }} baru</span>
                                </h5>
                            </div>
                            <div class="text-muted small mt-1">
                                <span><i class="fas fa-hashtag"></i> {{ t.ticket_number }}</span>
                                <span class="ml-3"><i class="fas fa-folder"></i> {{ categoryLabel(t.category) }}</span>
                                <span class="ml-3"><i class="fas fa-flag"></i> {{ priorityLabel(t.priority) }}</span>
                            </div>
                        </div>
                        <div class="col-md-3 text-right text-muted small">
                            <div>Dibuat: {{ formatDate(t.created_at) }}</div>
                            <div v-if="t.last_admin_reply_at">Update: {{ formatRelative(t.updated_at) }}</div>
                        </div>
                        <div class="col-md-2 text-right">
                            <i class="fas fa-chevron-right text-muted"></i>
                        </div>
                    </div>
                </router-link>
            </div>
        </div>

        <!-- New ticket modal -->
        <modal name="new_ticket_modal" :height="'auto'" :adaptive="true" :scrollable="true" :width="600">
            <div class="p-4">
                <h5><i class="fas fa-plus"></i> Buat Tiket Baru</h5>
                <hr>
                <div class="form-group">
                    <label>Subject *</label>
                    <input v-model="newTicket.subject" class="form-control" placeholder="Ringkasan singkat masalah" maxlength="255">
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Kategori</label>
                            <select v-model="newTicket.category" class="form-control">
                                <option value="general">Umum</option>
                                <option value="bug">Bug / Error</option>
                                <option value="billing">Pembayaran</option>
                                <option value="feature">Saran Fitur</option>
                                <option value="other">Lainnya</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label>Priority</label>
                            <select v-model="newTicket.priority" class="form-control">
                                <option value="low">Rendah</option>
                                <option value="normal">Normal</option>
                                <option value="high">Tinggi</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>Pesan *</label>
                    <textarea v-model="newTicket.message" class="form-control" rows="6" placeholder="Jelaskan masalah Anda secara detail..."></textarea>
                </div>
                <div class="form-group">
                    <label>Lampiran (max 5 file, 25MB/file — semua format termasuk video)</label>
                    <input ref="fileInputModal" type="file" multiple @change="onFileSelect" class="form-control" accept="*/*">
                    <div v-if="newTicket.files.length > 0" class="mt-2">
                        <div v-for="(f, i) in newTicket.files" :key="i" class="d-flex justify-content-between align-items-center small p-2" style="background:#f9fafb; border-radius:4px; margin-bottom:4px;">
                            <span><i class="fas fa-paperclip"></i> {{ f.name }} <span class="text-muted">({{ formatSize(f.size) }})</span></span>
                            <button @click="removeFile(i)" class="btn btn-sm btn-link text-danger p-0"><i class="fas fa-times"></i></button>
                        </div>
                    </div>
                </div>
                <div class="text-right">
                    <button @click="$modal.hide('new_ticket_modal')" class="btn btn-light mr-2">Batal</button>
                    <button @click="submitTicket" :disabled="submitting || !canSubmit" class="btn btn-primary">
                        <span v-if="submitting"><i class="fas fa-spinner fa-spin"></i> Mengirim...</span>
                        <span v-else><i class="fas fa-paper-plane"></i> Kirim Tiket</span>
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import moment from 'moment';
export default {
    data() {
        return {
            loading: false,
            submitting: false,
            tickets: [],
            workingHours: { is_open: true, info: 'Senin–Jumat, 10:00–20:00 WIB' },
            filter: { status: '', search: '' },
            newTicketModal: false,
            newTicket: { subject: '', category: 'general', priority: 'normal', message: '', files: [] },
            searchTimer: null,
        };
    },
    computed: {
        filteredTickets() {
            const s = (this.filter.search || '').toLowerCase();
            if (!s) return this.tickets;
            return this.tickets.filter(t =>
                (t.subject || '').toLowerCase().includes(s) ||
                (t.ticket_number || '').toLowerCase().includes(s)
            );
        },
        canSubmit() {
            return this.newTicket.subject.trim().length >= 5 && this.newTicket.message.trim().length >= 5;
        },
    },
    watch: {
        newTicketModal(v) {
            if (v) this.$modal.show('new_ticket_modal');
            else this.$modal.hide('new_ticket_modal');
        },
    },
    mounted() {
        this.loadList();
        this.loadWorkingHours();
    },
    methods: {
        async loadList() {
            this.loading = true;
            try {
                const params = {};
                if (this.filter.status) params.status = this.filter.status;
                const r = await this.$store.dispatch('support_ticket_list', params);
                this.tickets = r.data.data.tickets || [];
            } catch (e) {
                this.$toast.error('Gagal load tiket');
            }
            this.loading = false;
        },
        async loadWorkingHours() {
            try {
                const r = await this.$store.dispatch('support_ticket_working_hours');
                this.workingHours = r.data.data;
            } catch (e) { /* silent */ }
        },
        onSearchInput() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => { /* client-side filter via computed */ }, 300);
        },
        onFileSelect(e) {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
                if (f.size > 25 * 1024 * 1024) {
                    this.$toast.error(`File "${f.name}" melebihi 25MB`);
                    continue;
                }
                if (this.newTicket.files.length >= 5) {
                    this.$toast.error('Maksimal 5 file per pesan');
                    break;
                }
                this.newTicket.files.push(f);
            }
            e.target.value = '';
        },
        removeFile(i) { this.newTicket.files.splice(i, 1); },
        async submitTicket() {
            if (!this.canSubmit) return;
            this.submitting = true;
            try {
                const fd = new FormData();
                fd.append('subject', this.newTicket.subject.trim());
                fd.append('message', this.newTicket.message.trim());
                fd.append('category', this.newTicket.category);
                fd.append('priority', this.newTicket.priority);
                this.newTicket.files.forEach(f => fd.append('files', f));
                const r = await this.$store.dispatch('support_ticket_create', fd);
                this.$toast.success('Tiket dibuat: ' + r.data.data.ticket.ticket_number);
                this.newTicket = { subject: '', category: 'general', priority: 'normal', message: '', files: [] };
                this.newTicketModal = false;
                this.loadList();
                // Redirect ke detail
                this.$router.push('/support/ticket/' + r.data.data.ticket.id);
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal kirim tiket');
            }
            this.submitting = false;
        },
        statusLabel(s) {
            return { open: 'Open', in_progress: 'Diproses', waiting_user: 'Menunggu Anda', closed: 'Selesai' }[s] || s;
        },
        categoryLabel(c) {
            return { general: 'Umum', bug: 'Bug', billing: 'Pembayaran', feature: 'Saran Fitur', other: 'Lainnya' }[c] || c;
        },
        priorityLabel(p) {
            return { low: 'Rendah', normal: 'Normal', high: 'Tinggi', urgent: 'Urgent' }[p] || p;
        },
        formatDate(ts) { return ts ? moment(ts).format('DD MMM YYYY HH:mm') : '-'; },
        formatRelative(ts) { return ts ? moment(ts).fromNow() : ''; },
        formatSize(n) {
            if (n < 1024) return n + ' B';
            if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
            return (n / (1024 * 1024)).toFixed(1) + ' MB';
        },
    },
};
</script>

<style scoped>
.ticket-card {
    display: block;
    background: #fff;
    padding: 16px 20px;
    border-radius: 10px;
    margin-bottom: 10px;
    text-decoration: none;
    color: inherit;
    border: 1px solid #e5e7eb;
    transition: all 0.15s;
}
.ticket-card:hover {
    background: #f9fafb;
    border-color: #1976d2;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.08);
}
.ticket-subject {
    font-size: 16px;
    color: #1f2937;
    margin: 0;
}
.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
}
.status-open { background: #fef3c7; color: #92400e; }
.status-in_progress { background: #dbeafe; color: #1e40af; }
.status-waiting_user { background: #fce7f3; color: #9d174d; }
.status-closed { background: #d1fae5; color: #065f46; }
.unread-pill {
    background: #ef4444;
    color: #fff;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 6px;
    vertical-align: middle;
}
</style>
